import React, {useContext, useEffect, useMemo, useState} from 'react';
import {
    MaterialReactTable,
    useMaterialReactTable,
} from 'material-react-table';
import './MaterialUiRemoteTable.css'
import CsvDownloader from "react-csv-downloader";
import {Spinner} from "react-bootstrap";
import {SiMicrosoftexcel} from "react-icons/si";
import {MAX_PAGE_SIZE} from "../../Constants/constants";
import {store} from "../../Store/store";
import {findLabelByValue, increaseDateByOneDay, meterTypeFilterOptions, setIsPreparingExcelData} from "../../General/SharedMethods";
import {Box, IconButton, Tooltip} from "@mui/material";
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

const MaterialUiRemoteTable = ({
                                   columns,
                                   csvFileName,
                                   getData,
                                   columnFilters,
                                   setColumnFilters,
                                   additionalDataArguments,
                                   setIsErrorsLoaded,
                                   sorting,
                                   pagination,
                                   setSorting,
                                   setPagination,
                                   renderAdditionalCustomToolBarActions,
                                   customRowPerPageOptions,
                                   textFieldNames,
                                   excelColumns,
                                   columnVisibility,
                                   setColumnVisibility
                               }) => {
    const [data, setData] = useState([]);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefetching, setIsRefetching] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [totalPages, setTotalPages] = useState(null);
    const [isInitializing, setIsInitializing] = useState(false);
    const {dispatch} = useContext(store);

    const getPageDetails = (pagination) => {
        const pageNumber = pagination.pageIndex;
        const pageSize = pagination.pageSize;
        return {pageNumber, pageSize};
    }

    const buildColumnFiltersQuery = (columnFilters, columns) => {
        const enhancedFilters = columnFilters.map(filter => {
            const column = columns.find(col => col.accessorKey === filter.id);
            const filterType = column ? column.filterVariant : 'text';
            return {
                ...filter,
                type: filterType,
                column: column
            };
        });

        let columnFiltersQuery = enhancedFilters.length > 0 ? '' : null;
        enhancedFilters.forEach((filter, index) => {
            const filterId = filter.id.includes('.') ? filter.id.split('.')[0] : filter.id;
            columnFiltersQuery += index !== 0 ? ';' : '';
            switch (filter.type) {
                case 'select':
                    const filterValue = filter.column.enumArray.find((obj) => filter.value === obj.label);
                    if (filterId === 'connectionState' && filterValue.value === 'DISCONNECTED') {
                        columnFiltersQuery += `${filterId}=in=('DISCONNECTED','INTERRUPTED')`;
                    } else {
                        columnFiltersQuery += `${filterId}=='${filterValue.value}'`;
                    }
                    break;
                case 'number':
                    const numberValue = filter.value.number;
                    const numberOperator = filter.value.operator;
                    columnFiltersQuery += `${filterId}${numberOperator}'${numberValue}'`;
                    break;
                case 'date':
                    let dateValue = new Date(filter.value.date).toISOString();
                    const operator = filter.value.operator;
                    if (operator === '>') {
                        dateValue = increaseDateByOneDay(dateValue);
                    }
                    if (operator === '<=') {
                        dateValue = increaseDateByOneDay(dateValue);
                    }
                    if (dateValue.endsWith('z') || dateValue.endsWith('Z')) {
                        dateValue = dateValue.slice(0, -1);
                    }
                    columnFiltersQuery += `${filterId}${operator}'${dateValue}'`;
                    break;
                case 'multi-select':
                    let filteredItems = filter.column.enumArray.filter(obj => {
                            return filter.value.includes(obj.label)
                        }
                    )
                    filteredItems = filteredItems.map(it => `\'${it.value}\'`).join(',');
                    columnFiltersQuery += `${filterId}=in=(${filteredItems})`;
                    break;
                default:
                    columnFiltersQuery += `${filterId}=ilike='${filter.value}'`;
                    break;
            }
        });
        return columnFiltersQuery;
    }

    const fetchExcelData = async () => {
        let data = [];
        let length;
        setIsPreparingExcelData(dispatch, true);
        try {
            const filters = cleanColumnFilters(columnFilters);
            const columnFiltersQuery = buildColumnFiltersQuery(filters, columns);
            const sortQuery = sorting.length > 0 ? `${sorting[0].id},${sorting[0].desc ? 'desc' : 'asc'}` : null;
            const response = await getData(columnFiltersQuery, sortQuery, 0, MAX_PAGE_SIZE, ...additionalDataArguments);

            if (!response || !response.data || !Array.isArray(response.data.content)) {
                console.error("Invalid response or missing data content for Excel export");
                setIsPreparingExcelData(dispatch, false);
                return [];
            }

            for (const item of response.data.content) {
                for (const [key, value] of Object.entries(item)) {
                    if(key === 'ended'){
                        item[key] = !value;
                    }
                    if (key === 'meterType') {
                        item[key] = findLabelByValue(meterTypeFilterOptions(), value);
                    }
                    if (typeof value === 'string' && value.includes(',')) {
                        item[key] = value.replace(/,/g, '');
                    }
                }

                length = data.push(item);
                if (length >= 15000) {
                    break;
                }
            }
            setIsPreparingExcelData(dispatch, false);
            return data;
        } catch (error) {
            console.error("Error preparing Excel data:", error);
            setIsPreparingExcelData(dispatch, false);
            return [];
        }
    }

    const cleanColumnFilters = (filters) => {
        return filters.filter(filter => !(Array.isArray(filter.value) && filter.value.length === 0));
    }

    const fetchData = async () => {
        try {
            setIsError(false);
            if (!data.length) {
                setIsLoading(true);
            } else {
                setIsRefetching(true);
            }
            columnFilters = cleanColumnFilters(columnFilters);
            const columnFiltersQuery = buildColumnFiltersQuery(columnFilters, columns);
            const {pageNumber, pageSize} = getPageDetails(pagination);
            const sortQuery = sorting.length > 0 ? `${sorting[0].id},${sorting[0].desc ? 'desc' : 'asc'}` : null;
            setIsInitializing(true);
            setIsErrorsLoaded(false);
            const response = await getData(columnFiltersQuery, sortQuery, pageNumber, pageSize, ...additionalDataArguments);
            setIsErrorsLoaded(true);
            setIsInitializing(false);
            if (!response || !response.data) {
                setIsError(true);
                console.error("No response from server or missing data property");
                setRowCount(0);
                setTotalPages(0);
                setData([]);
            } else {
                setIsError(false)
                setRowCount(response.data.totalElements || 0);
                setTotalPages(response.data.totalPages || 0);

                if (textFieldNames && Array.isArray(response.data.content)) {
                    const modifiedContent = response.data.content.map(item => {
                        const newItem = {...item};
                        textFieldNames.forEach(fieldName => {
                            if (newItem[fieldName] === null) {
                                newItem[fieldName] = '';
                            }
                        });
                        return newItem;
                    });
                    setData(modifiedContent);
                } else if (Array.isArray(response.data.content)) {
                    setData(response.data.content);
                } else {
                    console.error("Response data content is not an array");
                    setData([]);
                }
            }
        } catch (error) {
            setIsError(true);
            console.error(error);
        }
        setIsLoading(false);
        setIsRefetching(false);
    }

    useEffect(() => {
        fetchData()
    }, [columnFilters, pagination.pageIndex, pagination.pageSize, sorting, ...additionalDataArguments]);

    const table = useMaterialReactTable({
        columns,
        data,
        muiTableBodyProps: {
            sx: {
                '& tr:nth-of-type(odd) > td': {
                    backgroundColor: 'action.hover',
                    whiteSpace: 'nowrap',
                },
                '& tr:nth-of-type(odd):not([data-selected="true"]):not([data-pinned="true"]):hover > td':
                    {
                        backgroundColor: 'grey.200',
                        whiteSpace: 'nowrap',
                    },
                '& tr:nth-of-type(even):not([data-selected="true"]):not([data-pinned="true"]):hover > td':
                    {
                        backgroundColor: 'grey.200',
                        whiteSpace: 'nowrap',
                    },
                '& td': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                },
            },
        },
        muiTableHeadProps: {
            sx: {
                whiteSpace: 'nowrap',
                '& th': {
                    whiteSpace: 'nowrap',
                },
                '& th .MuiTableSortLabel-root': {
                    whiteSpace: 'nowrap',
                },
                '& th .MuiTableSortLabel-root span': {
                    whiteSpace: 'nowrap',
                },
                '& th div': {
                    whiteSpace: 'nowrap',
                },
                '& .MuiTableCell-head': {
                    whiteSpace: 'nowrap',
                },
                '& .MuiTableCell-head span': {
                    whiteSpace: 'nowrap',
                },
                '& .MuiTableCell-head div': {
                    whiteSpace: 'nowrap',
                },
                '& .MuiTableCell-head button': {
                    whiteSpace: 'nowrap',
                },
                '& .MuiTableCell-head input': {
                    whiteSpace: 'nowrap',
                },
                '& .MuiTableCell-head select': {
                    whiteSpace: 'nowrap',
                },
                '& .MuiTableCell-head label': {
                    whiteSpace: 'nowrap',
                },
                '& .MuiTableCell-head p': {
                    whiteSpace: 'nowrap',
                },
                '& .MuiTableCell-head *': {
                    whiteSpace: 'nowrap',
                },
            }
        },
        muiPaginationProps: {
            ...(customRowPerPageOptions ? {rowsPerPageOptions: customRowPerPageOptions} : {}),
        },
        initialState: {
            showColumnFilters: true, 
            density: 'compact', 
            columnVisibility: columnVisibility || {lifecyclePhase: false}
        },
        enableFullScreenToggle: false,
        manualPagination: true,
        manualSorting: true,
        enableDensityToggle: false,
        enableGlobalFilter: false,
        enableFilterMatchHighlighting: false,
        muiFilterTextFieldProps: {
            placeholder: 'Filter',
        },
        renderTopToolbarCustomActions: () => <>
            {csvFileName && (
                <Tooltip title="Export to Excel">
                    <Box>
                        <CsvDownloader
                            disabled={isInitializing}
                            filename={csvFileName}
                            className={'btn export-to-excel-button'}
                            separator={","}
                            columns={excelColumns}
                            datas={
                                async () => {
                                    return await fetchExcelData();
                                }
                            }
                        >
                            {isInitializing ? <Spinner animation={'border'} variant={'primary'} size={"sm"}/> : <SiMicrosoftexcel size={15} style={{color: 'white'}}/>}
                        </CsvDownloader>
                    </Box>
                </Tooltip>
            )}
            {renderAdditionalCustomToolBarActions()}
            <Tooltip title="Clear All Filters">
                <IconButton
                    onClick={() => setColumnFilters([])}>
                    <FilterAltOffIcon/>
                </IconButton>
            </Tooltip>
        </>,
        muiToolbarAlertBannerProps: isError
            ? {
                color: 'error',
                children: 'Error loading data',
            }
            : undefined,
        onColumnFiltersChange: setColumnFilters,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnVisibilityChange: setColumnVisibility,
        rowCount,
        state: {
            columnFilters,
            isLoading,
            pagination,
            showAlertBanner: isError,
            showProgressBars: isRefetching,
            showLoadingOverlay: isLoading,
            sorting,
            columnVisibility,
        },

    });

    return <Box pr={1.5} pl={1.5} className="materialReactTableToolbarRightAlign">
        <MaterialReactTable table={table}/>
    </Box>;
};

export default MaterialUiRemoteTable;
